import { computed, markRaw, ref } from 'vue'
import { defineStore } from 'pinia'
import type { AiraController, AiraPropertyValue, AiraQueuedWrite } from '../types'

/**
 * Single source of truth for the floating Aira widget.
 *
 * Pages never touch the Rive runtime directly — they push declarative state here
 * (visibility, hint text, view model properties) and the mounted widget applies it
 * to the bound ViewModelInstance. Writes requested before the .riv finishes loading
 * are queued and flushed on bind.
 */
export const useAiraWidget = defineStore('airaWidget', () => {
  const isVisible = ref(true)
  const isReady = ref(false)
  const hint = ref<string | null>(null)

  const controller = ref<AiraController | null>(null)
  const queue = ref<AiraQueuedWrite[]>([])
  const propertyNames = ref<string[]>([])

  /**
   * Viewport point Aira is currently flying to, or `null` when she sits in her
   * home corner. Stored as a plain point so the widget stays the only place that
   * knows how to translate it into a transform.
   */
  const anchor = ref<{ x: number; y: number } | null>(null)

  /**
   * Viewport point the eyes should follow instead of the cursor, or `null` to go
   * back to tracking the pointer.
   */
  const gazeTarget = ref<{ x: number; y: number } | null>(null)

  /** Game-over screen after she walks into the camera. */
  const isBlackout = ref(false)

  /**
   * Scale applied to the whole widget. Driving the "she is coming for you" finale
   * through a scale keeps it a pure CSS transition on the same transform the flight
   * already uses.
   */
  const scale = ref(1)

  /** Eyes locked forward, at the viewer, instead of at the cursor or a target. */
  const isLookingAhead = ref(false)

  /** Angry wobble, driven from CSS rather than the artboard. */
  const isShaking = ref(false)
  let shakeTimer: ReturnType<typeof setTimeout> | null = null

  const hasHint = computed(() => Boolean(hint.value))

  /** Called by the widget once the view model instance is bound. */
  const registerController = (next: AiraController) => {
    controller.value = markRaw(next)
    propertyNames.value = next.propertyNames()
    isReady.value = true

    for (const item of queue.value) {
      next.write(item.path, item.value)
    }
    queue.value = []
  }

  const unregisterController = () => {
    controller.value = null
    propertyNames.value = []
    isReady.value = false
  }

  /** Set a data binding property, or fire a trigger when `value` is undefined. */
  const write = (path: string, value?: AiraPropertyValue) => {
    if (!controller.value) {
      queue.value.push({ path, value })
      return
    }
    controller.value.write(path, value)
  }

  const trigger = (path: string) => write(path)

  const show = () => {
    isVisible.value = true
  }

  const hide = () => {
    isVisible.value = false
  }

  /** Show a speech bubble next to Aira; `null` clears it. */
  const say = (text: string | null) => {
    hint.value = text
  }

  const clearHint = () => {
    hint.value = null
  }

  /** Send Aira to a point in viewport coordinates. */
  const flyTo = (point: { x: number; y: number }) => {
    anchor.value = point
  }

  /** Send Aira back to her home corner. */
  const flyHome = () => {
    anchor.value = null
  }

  /** Lock the eyes onto a viewport point, overriding cursor tracking. */
  const lookAt = (point: { x: number; y: number } | null) => {
    gazeTarget.value = point
  }

  /** Release the lock and follow the cursor again. */
  const lookAtCursor = () => {
    gazeTarget.value = null
  }

  /** Shake her for `duration` ms; a second call restarts the timer. */
  const shake = (duration = 900) => {
    if (shakeTimer) clearTimeout(shakeTimer)
    isShaking.value = true
    shakeTimer = setTimeout(() => {
      isShaking.value = false
      shakeTimer = null
    }, duration)
  }

  /**
   * Walk into the camera: eyes forward, then grow. The widget sets the actual scale
   * via `setScale`, because only it knows its rendered size and can size the finale
   * against the viewport instead of a hardcoded multiplier.
   */
  const approach = () => {
    isLookingAhead.value = true
  }

  const setScale = (value: number) => {
    scale.value = value
  }

  const endApproach = () => {
    isLookingAhead.value = false
    scale.value = 1
  }

  const showBlackout = () => {
    isBlackout.value = true
  }

  const hideBlackout = () => {
    isBlackout.value = false
  }

  const stopShaking = () => {
    if (shakeTimer) clearTimeout(shakeTimer)
    shakeTimer = null
    isShaking.value = false
  }

  /** Reset per-page contextual state without unmounting the widget. */
  const reset = () => {
    hint.value = null
    isVisible.value = true
    anchor.value = null
    gazeTarget.value = null
    stopShaking()
    isBlackout.value = false
    isLookingAhead.value = false
    scale.value = 1
  }

  return {
    isVisible,
    isReady,
    hint,
    hasHint,
    propertyNames,
    anchor,
    gazeTarget,
    isShaking,
    isBlackout,
    scale,
    isLookingAhead,
    approach,
    endApproach,
    setScale,
    showBlackout,
    hideBlackout,
    flyTo,
    flyHome,
    lookAt,
    lookAtCursor,
    shake,
    stopShaking,
    registerController,
    unregisterController,
    write,
    trigger,
    show,
    hide,
    say,
    clearHint,
    reset,
  }
})

export const useAiraWidgetStore = useAiraWidget
